import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { CornerDownLeft, Search, User, Users, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SensitiveRecordBadge } from '@/components/patient/SensitiveRecordBadge';
import { cn } from '@/lib/utils';

/**
 * Pure UI result contract. STEP 7 (PTV2-916) maps
 * ``PatientSummaryViewModel[]`` to this shape; the dialog itself stays
 * agnostic to the underlying query.
 */
export interface QuickSearchResult {
  readonly id: string;
  readonly label: string;
  readonly sublabel: string;
  readonly initials: string;
  readonly href: string;
  /** Sensitive / break-glass record — drives the row badge + accent. */
  readonly sensitive: boolean;
}

interface QuickSearchProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  /** Patient results (first section). */
  readonly results: readonly QuickSearchResult[];
  /** Family results (second section). Optional; omitted → no Families section. */
  readonly familyResults?: readonly QuickSearchResult[];
  readonly isLoading: boolean;
  /**
   * Called when the user activates a result (click / Enter). Parent
   * decides how to navigate.
   */
  readonly onSelect: (result: QuickSearchResult) => void;
  /**
   * Optional "create new patient" affordance. When omitted, the footer
   * action is hidden entirely.
   */
  readonly onCreateNew?: (query: string) => void;
}

/**
 * Global cmd-k style search palette. Three stacked surfaces on a single
 * card: borderless search row, scrollable results listbox, keyboard
 * footer. Anchored toward the top of the viewport so it lands where
 * users expect a search to be — not centered like a confirmation modal.
 *
 * Patients and Families render as two sections over a single flat option list,
 * so arrow-key navigation and `aria-activedescendant` run across both.
 */
export function QuickSearch({
  open,
  onOpenChange,
  query,
  onQueryChange,
  results,
  familyResults = [],
  isLoading,
  onSelect,
  onCreateNew,
}: QuickSearchProps): ReactElement {
  const { t } = useTranslation();
  const listboxId = useId();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Flat option list across both sections — indices drive keyboard nav.
  const items = useMemo(
    () => [...results, ...familyResults],
    [results, familyResults],
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [items, query]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, Math.max(items.length - 1, 0)));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (event.key === 'Enter') {
        const target = items[selectedIndex];
        if (target !== undefined) {
          event.preventDefault();
          onSelect(target);
        }
      }
    },
    [items, onSelect, selectedIndex],
  );

  const trimmedQuery = query.trim();
  const hasResults = items.length > 0;
  const showEmpty = trimmedQuery.length > 0 && !isLoading && !hasResults;
  const showIdle = trimmedQuery.length === 0 && !isLoading && !hasResults;
  const showCreateNew = onCreateNew !== undefined;

  const renderRow = (result: QuickSearchResult, index: number): ReactElement => (
    <button
      type="button"
      key={result.id}
      id={`${listboxId}-option-${String(index)}`}
      role="option"
      aria-selected={index === selectedIndex}
      onClick={() => {
        onSelect(result);
      }}
      onMouseEnter={() => {
        setSelectedIndex(index);
      }}
      className={cn(
        'w-full flex items-center gap-3 px-2.5 py-2 rounded-md text-left transition-colors cursor-pointer',
        index === selectedIndex ? 'bg-accent text-accent-foreground' : 'text-foreground',
        result.sensitive && 'border-l-2 border-l-destructive',
      )}
    >
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className="bg-brand text-brand-foreground text-[11px] font-semibold">
          {result.initials}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-medium leading-tight">
          <span className="truncate">{result.label}</span>
          {result.sensitive && <SensitiveRecordBadge />}
        </p>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{result.sublabel}</p>
      </div>
      {index === selectedIndex && (
        <CornerDownLeft
          className="h-3.5 w-3.5 text-muted-foreground shrink-0"
          aria-hidden="true"
        />
      )}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'p-0 gap-0 max-w-xl overflow-hidden rounded-xl shadow-2xl',
          'top-[12vh] translate-y-0',
          '[&>button]:hidden',
        )}
      >
        <DialogTitle className="sr-only">{t('quickSearch.title')}</DialogTitle>
        <DialogDescription className="sr-only">
          {t('quickSearch.description')}
        </DialogDescription>

        <div className="flex items-center gap-3 px-4 h-14 border-b border-border">
          <Search
            className="h-4 w-4 text-muted-foreground shrink-0"
            aria-hidden="true"
          />
          <input
            type="text"
            value={query}
            onChange={(event) => {
              onQueryChange(event.target.value);
            }}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              hasResults ? `${listboxId}-option-${String(selectedIndex)}` : undefined
            }
            placeholder={t('quickSearch.placeholder')}
            // A command palette must focus its input on open — that's the whole
            // point of the shortcut; focusing here is the expected affordance.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            className="flex-1 bg-transparent border-0 outline-none text-[15px] text-foreground placeholder:text-muted-foreground"
          />
        </div>

        <div
          id={listboxId}
          role="listbox"
          aria-label={t('quickSearch.resultsAriaLabel')}
          className="max-h-[60vh] min-h-[180px] overflow-y-auto"
        >
          {isLoading && (
            <p className="text-center py-10 px-6 text-sm text-muted-foreground">
              {t('quickSearch.loading')}
            </p>
          )}

          {showIdle && (
            <p className="text-center py-10 px-6 text-sm text-muted-foreground">
              {t('quickSearch.startTyping')}
            </p>
          )}

          {showEmpty && (
            <div className="text-center py-10 px-6">
              <p className="text-sm font-medium text-foreground">
                {t('quickSearch.empty', { query: trimmedQuery })}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('quickSearch.emptyHint')}
              </p>
            </div>
          )}

          {results.length > 0 && (
            <div className="py-2">
              <SectionHeader
                icon={<User className="h-3 w-3" aria-hidden="true" />}
                label={t('quickSearch.sections.patients')}
              />
              <div className="px-2">{results.map((r, i) => renderRow(r, i))}</div>
            </div>
          )}

          {familyResults.length > 0 && (
            <div className="pb-2">
              <SectionHeader
                icon={<Users className="h-3 w-3" aria-hidden="true" />}
                label={t('quickSearch.sections.families')}
              />
              <div className="px-2">
                {familyResults.map((r, i) => renderRow(r, results.length + i))}
              </div>
            </div>
          )}
        </div>

        {showCreateNew && (
          <div className="border-t border-border">
            <button
              type="button"
              onClick={() => {
                onCreateNew(query);
              }}
              className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-accent flex items-center gap-2.5 transition-colors cursor-pointer"
            >
              <UserPlus
                className="h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              {trimmedQuery.length > 0
                ? t('quickSearch.createNewWithQuery', { query: trimmedQuery })
                : t('quickSearch.createNew')}
            </button>
          </div>
        )}

        <div className="border-t border-border bg-muted/40 px-4 h-9 flex items-center gap-4 text-[11px] text-muted-foreground">
          <FooterHint keys={['↑', '↓']} label={t('quickSearch.footer.navigate')} />
          <FooterHint keys={['↵']} label={t('quickSearch.footer.select')} />
          <FooterHint keys={['esc']} label={t('quickSearch.footer.close')} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SectionHeader({
  icon,
  label,
}: {
  readonly icon: ReactElement;
  readonly label: string;
}): ReactElement {
  return (
    <div className="px-4 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground flex items-center gap-1.5">
      {icon}
      {label}
    </div>
  );
}

interface FooterHintProps {
  readonly keys: readonly string[];
  readonly label: string;
}

function FooterHint({ keys, label }: FooterHintProps): ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex items-center gap-0.5">
        {keys.map((key) => (
          <kbd
            key={key}
            className="inline-flex h-4 min-w-4 px-1 items-center justify-center rounded border border-border bg-background font-mono text-[10px] font-medium text-foreground"
          >
            {key}
          </kbd>
        ))}
      </span>
      {label}
    </span>
  );
}
